import type { VectorElement } from '@/components/canvas/document/publicTypes';
import { getVectorPath } from '@/components/canvas/elements/shape/vector/vectorPaths';
import { elementFillCss, firstShadow, firstStroke } from '@/components/canvas/elements/model';
import { drawShapeText } from '../utils/shapeTextExport';

export async function drawVector(ctx: CanvasRenderingContext2D, el: VectorElement) {
  const { x, y } = el.layout;
  const { width, height } = el.size;
  const fillColor = elementFillCss(el);
  const stroke = firstStroke(el.appearance);
  const shadow = firstShadow(el.appearance);
  const strokeWidth = stroke?.width ?? 0;

  const pathD = getVectorPath(el.data.shape, {
    polygonSides: el.data.polygonSides,
    starPoints: el.data.starPoints,
    starInnerRatio: el.data.starInnerRatio,
    pathData: el.data.pathData,
  });

  if (!pathD) return;

  // Scale factor from 0–100 viewBox to canvas pixels
  const scaleX = width / 100;
  const scaleY = height / 100;

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scaleX, scaleY);

  if (shadow) {
    ctx.shadowColor = shadow.color;
    ctx.shadowBlur = shadow.blur;
    ctx.shadowOffsetX = shadow.offsetX;
    ctx.shadowOffsetY = shadow.offsetY;
  }

  const path = new Path2D(pathD);

  // Fill — 'none' means no fill
  if (fillColor && fillColor !== 'none' && fillColor !== 'transparent') {
    ctx.fillStyle = fillColor;
    ctx.fill(path);
  }

  // Reset shadow before stroke
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  if (stroke && strokeWidth > 0 && stroke.color) {
    ctx.strokeStyle = stroke.color;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    // strokeWidth is stored in screen pixels (matches vectorEffect="non-scaling-stroke").
    // Inside ctx.scale(scaleX, scaleY), compensate with the geometric mean so the
    // visual stroke width matches the DOM renderer at export scale = 1.
    const avgScale = Math.sqrt(scaleX * scaleY);
    const lw = strokeWidth / avgScale;
    ctx.lineWidth = lw;

    if (stroke.style === 'dashed') ctx.setLineDash([lw * 4, lw * 2]);
    else if (stroke.style === 'dotted') ctx.setLineDash([lw, lw]);
    else ctx.setLineDash([]);

    ctx.stroke(path);
    ctx.setLineDash([]);
  }

  ctx.restore();

  if (el.data.text) {
    drawShapeText(ctx, el, { x, y, width, height });
  }
}
